// IATA / ICAO airline code → Arabic airline name.
// Used to derive "شركة الطيران" from a raw flight_number (e.g. "EK 803" → طيران الإمارات).
const AIRLINE_MAP = {
  // GCC + السعودية
  SV: "الخطوط السعودية", SVA: "الخطوط السعودية",
  XY: "طيران ناس", NAS: "طيران ناس",
  F3: "طيران أديل",
  EK: "طيران الإمارات", UAE: "طيران الإمارات",
  EY: "الاتحاد للطيران", ETD: "الاتحاد للطيران",
  FZ: "فلاي دبي", FDB: "فلاي دبي",
  G9: "العربية للطيران", ABY: "العربية للطيران",
  IY: "اليمنية",
  QR: "القطرية", QTR: "القطرية",
  WY: "الطيران العماني", OMA: "الطيران العماني",
  GF: "طيران الخليج", GFA: "طيران الخليج",
  KU: "الخطوط الكويتية", KAC: "الخطوط الكويتية",
  J9: "طيران الجزيرة", JZR: "طيران الجزيرة",
  RJ: "الملكية الأردنية",
  ME: "طيران الشرق الأوسط",
  MS: "مصر للطيران", MSR: "مصر للطيران",
  IX: "إير إنديا إكسبريس",
  AI: "إير إنديا",
  "6E": "إنديجو", IGO: "إنديجو",
  SG: "سبايس جت", SEJ: "سبايس جت",
  // آسيا/أوروبا/أمريكا
  TK: "الخطوط التركية", THY: "الخطوط التركية",
  PC: "بيغاسوس",
  LH: "لوفتهانزا", BA: "الخطوط البريطانية",
  AF: "إير فرانس", KL: "كي إل إم",
  LX: "الخطوط السويسرية", OS: "الخطوط النمساوية",
  SU: "أيروفلوت",
  CA: "الصينية الدولية", MU: "الصينية الشرقية", CZ: "الصينية الجنوبية",
  HO: "جونياو الصينية", GS: "تيانجين الصينية",
  CX: "كاثي باسيفيك", JL: "اليابانية", NH: "أول نيبون",
  KE: "الكورية", SQ: "سنغافورة", TG: "التايلاندية",
  QF: "كوانتاس",
  ET: "الإثيوبية",
  W6: "ويز إير", U2: "إيزي جيت", FR: "رايان إير",
  FV: "فلاي وان",
  YM: "مونتنيغرو",
  FP: "فري بيرد",
  FG: "أريانا أفغانستان",
  GH: "غلوبال إيرويز",
  GK: "جيت ستار اليابانية",
  HP: "هوبا أوروبا",
};

// Extract the leading airline code from a flight_number string.
// Handles both IATA (2-char, e.g. "EK", "G9", "6E") and ICAO (3-letter,
// e.g. "UAE", "SVA") prefixes. Prefers whichever variant exists in the
// airline map; otherwise falls back to the IATA-shaped code so callers can
// still show something useful for unknown carriers.
// "EK 803" → "EK", "sv1023" → "SV", "6E 202" → "6E", "999" → null.
export function extractAirlineCode(flightNumber) {
  if (!flightNumber) return null;
  const s = String(flightNumber).trim().toUpperCase().replace(/\s+/g, "");
  if (!s) return null;
  // IATA — exactly 2 chars, at least one letter (letter+letter, letter+digit,
  // digit+letter). Pure "99" is rejected.
  const iata = s.match(/^([A-Z][A-Z0-9]|[0-9][A-Z])/);
  // ICAO — exactly 3 letters, no digits.
  const icao = s.match(/^([A-Z]{3})/);
  if (icao && AIRLINE_MAP[icao[1]]) return icao[1];
  if (iata && AIRLINE_MAP[iata[1]]) return iata[1];
  if (iata) return iata[1];
  if (icao) return icao[1];
  return null;
}

// Return the Arabic airline name, or `null` if the code is unknown.
export function getAirlineName(flightNumber) {
  const code = extractAirlineCode(flightNumber);
  if (!code) return null;
  return AIRLINE_MAP[code] || null;
}

// Return the Arabic airline name if known, otherwise fall back to the raw
// IATA code so we always show *something* useful next to a flight number.
// Empty string when the flight_number itself is missing.
export function getAirlineNameOrCode(flightNumber) {
  const code = extractAirlineCode(flightNumber);
  if (!code) return "";
  return AIRLINE_MAP[code] || code;
}


// ─── Booking-level helpers ─────────────────────────────────────────────
// Multi-car bookings store flight info inside routes[0] as well as at the
// root level. These helpers apply the correct priority everywhere the UI
// displays / exports / sends flight info (tables, WhatsApp, Excel, PDFs).

// Priority: b.flight_airline (manual, root) → routes[0].flight_airline (manual, route)
//         → getAirlineNameOrCode(b.flight_number) (auto, root)
//         → getAirlineNameOrCode(routes[0].flight_number) (auto, route)
export function getBookingAirline(b) {
  if (!b) return "";
  if (b.flight_airline) return b.flight_airline;
  const route0 = Array.isArray(b.routes) ? b.routes[0] : null;
  if (route0?.flight_airline) return route0.flight_airline;
  const flightNo = b.flight_number || route0?.flight_number || "";
  return getAirlineNameOrCode(flightNo) || "";
}

// Priority: b.flight_number → routes[0].flight_number.
export function getBookingFlightNumber(b) {
  if (!b) return "";
  const route0 = Array.isArray(b.routes) ? b.routes[0] : null;
  return b.flight_number || route0?.flight_number || "";
}
